// Reveal parts for the v2.2 formats, shared by the phone's answer view and (where they fit) the stage:
//   CreditSplit      how the room scored on number, order and multi 'all': full, some and no marks, as one bar
//   NumberHistogram  how the room answered a number question, with the true value and this player's mark
//   CreditBadge      a small chip naming what one answer earned
// They take plain props (no store), so the stage can render them from a snapshot alone.
import type { CSSProperties } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { Reveal } from '@shared/types';
import { CREDITS, creditShares, type Credit } from './credit';
import { formatNumber } from './numberInput';
import { CrossGlyph, TickGlyph } from './parts';

export const CREDIT_LABEL: Record<Credit, string> = { full: 'Full marks', partial: 'Some marks', none: 'No marks' };

function CreditIcon({ credit, size = 14 }: { credit: Credit; size?: number }) {
  if (credit === 'full') return <TickGlyph size={size} stroke={3.4} />;
  if (credit === 'none') return <CrossGlyph size={size} stroke={3.4} />;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={3.4} strokeLinecap="round">
      <path d="M5.5 12h13" />
    </svg>
  );
}

export function CreditBadge({ credit, className = '' }: { credit: Credit; className?: string }) {
  return (
    <span className={`fx-credit-badge ${className}`} data-credit={credit}>
      <CreditIcon credit={credit} />
      {CREDIT_LABEL[credit]}
    </span>
  );
}

/** One stacked bar (full, some, none) with a legend that names each part and its share, so nothing relies on colour. */
export function CreditSplit({ credit, mine, title = 'How the room scored' }: { credit: NonNullable<Reveal['credit']>; mine?: Credit; title?: string }) {
  const reduce = useReducedMotion() ?? false;
  const shares = creditShares(credit);
  const total = credit.full + credit.partial + credit.none;
  const spoken = total
    ? `${title}: ${CREDITS.map((k) => `${CREDIT_LABEL[k].toLowerCase()} ${shares[k]}%`).join(', ')}.${mine ? ` You: ${CREDIT_LABEL[mine].toLowerCase()}.` : ''}`
    : `${title}: nobody answered.`;
  return (
    <figure className="fx-split" aria-label={spoken} role="group">
      <figcaption className="fx-split-title">{title}</figcaption>
      <div className="fx-split-bar" aria-hidden="true">
        {total > 0 && CREDITS.filter((k) => credit[k] > 0).map((k, i) => (
          <motion.i key={k} data-credit={k} style={{ flexGrow: credit[k] } as CSSProperties}
            initial={reduce ? { opacity: 0 } : { scaleX: 0, opacity: 1 }} animate={{ scaleX: 1, opacity: 1 }}
            transition={reduce ? { duration: 0.2 } : { type: 'spring', stiffness: 160, damping: 22, delay: 0.15 + i * 0.08 }} />
        ))}
      </div>
      <ul className="fx-split-key" aria-hidden="true">
        {CREDITS.map((k) => (
          <li key={k} data-credit={k} data-mine={mine === k ? '' : undefined}>
            <span className="fx-split-swatch"><CreditIcon credit={k} size={11} /></span>
            <span>{CREDIT_LABEL[k]}</span>
            <b>{shares[k]}%</b>
            {mine === k && <span className="fx-split-you">You</span>}
          </li>
        ))}
      </ul>
    </figure>
  );
}

type Bucket = { from: number; to: number; count: number };

/**
 * Which bucket holds a value. Buckets sit on the keypad's grid (steps of 10^-decimals) and include both ends, the
 * first starting at the keypad's minimum and the last ending at its maximum (the server's numberHistogram).
 * -1 below the first, the bucket count above the last.
 */
export function bucketOf(buckets: readonly Bucket[], v: number, decimals = 0): number {
  if (!buckets.length || !Number.isFinite(v)) return -1;
  const half = 0.5 * 10 ** -Math.max(0, decimals);
  if (v < buckets[0].from - half) return -1;
  for (let i = 0; i < buckets.length; i++) if (v < buckets[i].to + half) return i;
  return buckets.length;
}

/**
 * Axis names for the buckets: a bucket of one value is that value; the first and last catch-alls read "≤1" and
 * "≥11"; any other range is named by its lower bound (the table twin spells every range out).
 */
export function bucketLabels(buckets: readonly Bucket[], decimals: number): string[] {
  const f = (v: number) => formatNumber(v, decimals);
  return buckets.map((b, i) => {
    if (b.from === b.to) return f(b.from);
    if (i === 0 && buckets.length > 1) return `≤${f(b.to)}`;
    if (i === buckets.length - 1 && buckets.length > 1) return `≥${f(b.from)}`;
    return f(b.from);
  });
}
const rangeName = (b: Bucket, decimals: number) => (b.from === b.to ? formatNumber(b.from, decimals) : `${formatNumber(b.from, decimals)} to ${formatNumber(b.to, decimals)}`);

/**
 * How the room answered a number question. One series, so no legend box: the answer's column is solid emerald and
 * named, this player's column carries a "You" pin, every other column is a quiet neutral. Values are listed for
 * screen readers in a table twin.
 */
export function NumberHistogram({ buckets, answer, mine, decimals, unit }: { buckets: readonly Bucket[]; answer: number; mine?: number; decimals: number; unit?: string }) {
  const reduce = useReducedMotion() ?? false;
  if (!buckets.length) return null;
  const peak = Math.max(1, ...buckets.map((b) => b.count));
  const at = bucketOf(buckets, answer, decimals);
  const mineAt = typeof mine === 'number' ? bucketOf(buckets, mine, decimals) : undefined;
  const labels = bucketLabels(buckets, decimals);
  // Every column is named when the names are short; otherwise the answer, yours and the two ends, never side by side.
  const shown = new Set<number>();
  if (buckets.length <= 6 || labels.every((l) => l.length <= 3)) buckets.forEach((_, i) => shown.add(i));
  else for (const i of [at, mineAt ?? -1, 0, buckets.length - 1]) if (i >= 0 && i < buckets.length && ![...shown].some((j) => Math.abs(j - i) <= 1)) shown.add(i);
  const showLabel = (i: number) => shown.has(i);
  const unitText = unit ? ` ${unit}` : '';
  return (
    <figure className="fx-histo" role="group" aria-label="How the room answered">
      <figcaption className="fx-histo-title">How the room answered</figcaption>
      <div className="fx-histo-plot" aria-hidden="true" style={{ '--n': buckets.length } as CSSProperties}>
        {buckets.map((b, i) => {
          const isAnswer = i === at; const isMine = i === mineAt;
          return (
            <div key={i} className="fx-histo-col" data-answer={isAnswer ? '' : undefined} data-mine={isMine ? '' : undefined}>
              <div className="fx-histo-pins">
                {isMine && <span className="fx-histo-pin" data-kind="mine">You</span>}
                {isAnswer && <span className="fx-histo-pin" data-kind="answer"><TickGlyph size={10} stroke={4} />{b.count.toLocaleString('en-GB')}</span>}
              </div>
              <div className="fx-histo-track">
                <motion.i style={{ height: `${b.count ? Math.max(3, (100 * b.count) / peak) : 0}%` }}
                  initial={reduce ? { opacity: 0 } : { scaleY: 0 }} animate={{ scaleY: 1, opacity: 1 }}
                  transition={reduce ? { duration: 0.2 } : { type: 'spring', stiffness: 170, damping: 20, delay: 0.1 + i * 0.03 }} />
              </div>
              <span className="fx-histo-x">{showLabel(i) ? labels[i] : ''}</span>
            </div>
          );
        })}
      </div>
      {mineAt !== undefined && (mineAt < 0 || mineAt >= buckets.length) && (
        <p className="fx-histo-off">Your answer, {formatNumber(mine!, decimals)}{unitText}, is off the chart.</p>
      )}
      <div className="sr-only">
        <table>
          <caption>Players by answer. The answer is {formatNumber(answer, decimals)}{unitText}.</caption>
          <tbody>
            {buckets.map((b, i) => (
              <tr key={i}><th scope="row">{rangeName(b, decimals)}{unitText}</th><td>{b.count} {b.count === 1 ? 'player' : 'players'}{i === at ? ', the answer' : ''}{i === mineAt ? ', your answer' : ''}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
